// src/components/sections/Projects.js
import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { projectsData } from '../../data/projects';
import { motion } from 'framer-motion';
import ProjectDetail from './ProjectDetail';
import { Github } from 'lucide-react';

const ProjectCard = ({ project, index, onOpenDetail }) => {
  const { darkMode } = useTheme();
  const { language } = useLanguage();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className={`rounded-lg overflow-hidden shadow-lg transition-transform duration-300 transform hover:-translate-y-2 ${
        darkMode ? 'bg-gray-800' : 'bg-white'
      }`}
    >
      <div className="h-48 bg-gray-300 relative overflow-hidden">
        <img 
          src={project.image} 
          alt={project.title[language]} 
          className="w-full h-full object-cover"
        />
        <div className={`absolute inset-0 flex items-center justify-center bg-opacity-70 ${
          darkMode ? 'bg-gray-900' : 'bg-gray-800'
        } opacity-0 hover:opacity-100 transition-opacity duration-300`}>
          <button 
            onClick={() => onOpenDetail(project.id)}
            className={`px-4 py-2 rounded ${
              darkMode ? 'bg-blue-600' : 'bg-blue-500'
            } text-white`}
          >
            {language === 'fr' ? 'Voir détails' : 'View details'}
          </button>
        </div>
      </div>
      <div className="p-5">
        <h3 className="text-xl font-semibold mb-2">{project.title[language]}</h3>
        <p className={`mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {project.description[language]}
        </p>
        <div className="flex flex-wrap gap-2">
          {project.tags.map((tag, tagIndex) => {
            // Définir une couleur en fonction du tag
            let colorClass;
            if (tag.includes('Spring')) {
              colorClass = darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800';
            } else if (tag.includes('React')) {
              colorClass = darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800';
            } else if (tag.includes('Docker') || tag.includes('Kubernetes') || tag.includes('CI/CD')) {
              colorClass = darkMode ? 'bg-orange-900 text-orange-300' : 'bg-orange-100 text-orange-800';
            } else {
              colorClass = darkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-800';
            }
            
            return (
              <span 
                key={tagIndex} 
                className={`px-3 py-1 rounded-full text-xs ${colorClass}`}
              >
                {tag}
              </span>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};

const Projects = () => {
  const { darkMode } = useTheme();
  const { language } = useLanguage();
  const [selectedProject, setSelectedProject] = useState(null);
  const [ setSelectedProjectId] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Ouvrir les détails d'un projet
  const handleOpenDetail = (projectId) => {
    const index = projectsData.findIndex(p => p.id === projectId);
    const project = projectsData.find(p => p.id === projectId);
    
    if (project) {
      setSelectedProject(project);
      setSelectedProjectId(projectId);
      setCurrentIndex(index);
    }
  };
  
  // Fermer les détails d'un projet ou passer au suivant
  const handleCloseDetail = (nextProjectId) => {
    if (nextProjectId) {
      handleOpenDetail(nextProjectId);
    } else {
      setSelectedProject(null);
      setSelectedProjectId(null);
    }
  };
  
  return (
    <section id="projets" className="py-16">
      <div className="container mx-auto px-6">
        <motion.h2 
          className="text-3xl font-bold mb-12 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          {language === 'fr' ? 'Mes Projets' : 'My Projects'}
        </motion.h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projectsData.map((project, index) => (
            <ProjectCard 
              key={project.id} 
              project={project} 
              index={index}
              onOpenDetail={handleOpenDetail}
            />
          ))}
        </div>
        
        <motion.div 
          className="flex justify-center mt-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <a 
            href="https://github.com/igorvannel" 
            target="_blank" 
            rel="noopener noreferrer" 
            className={`px-6 py-3 rounded-lg ${
              darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
            } transition-colors duration-200 flex items-center`}
          >
            <Github className="mr-2 h-5 w-5" />
            {language === 'fr' ? 'Voir plus de projets sur GitHub' : 'See more projects on GitHub'}
          </a>
        </motion.div>
      </div>
      
      {/* Modal de détails du projet */}
      {selectedProject && (
        <ProjectDetail 
          project={selectedProject}
          onClose={handleCloseDetail}
          projectsData={projectsData}
          currentIndex={currentIndex}
        />
      )}
    </section>
  );
};

export default Projects;